import { useState } from "react";
import {
  AppBar,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AssignmentOutlined,
  CalendarMonthOutlined,
  ChecklistRtlOutlined,
  MarkEmailReadOutlined,
  PersonSearchOutlined,
  SchoolOutlined,
  ExpandLess,
  ExpandMore,
  FactCheckOutlined,
  GroupsOutlined,
  HelpOutlineOutlined,
  HomeOutlined,
  LightbulbOutlined,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  PersonOutlined,
  TopicOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { NavLink, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import AvisoSugerencias from "./AvisoSugerencias";
import DccLogo from "../public/DccLogo";
import BotonTema from "../BotonTema";
import Contador from "../kit/Contador";
import { tokens } from "../../theme";
import { usePendientesProcesos } from "../../queries/usePendientesProcesos";
import { useTareasExamen } from "../../queries/useTareas";
import { useExternosPendientes } from "../../queries/usePersonas";
import { useAuthStore } from "../../stores/authStore";
import SesionExpirada from "../auth/SesionExpirada";

const DRAWER_WIDTH = 236;

interface NavEntry {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
  /** Contador al final del ítem (p. ej. lo que espera acción en Mis Procesos). */
  badge?: React.ReactNode;
}

/** Ítem de navegación (enlace). `indent` lo sangra como hijo de un subgrupo. */
function NavItem({ item, onNavigate, indent = false }: { item: NavEntry; onNavigate: () => void; indent?: boolean }) {
  return (
    <ListItemButton
      component={NavLink}
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      sx={{
        borderRadius: `${tokens.barra.radioItem}px`,
        mb: 0.5,
        pl: indent ? 3.25 : 2,
        color: tokens.barra.itemTexto,
        "& .MuiListItemIcon-root": { color: "inherit", minWidth: indent ? 32 : 38 },
        "&:hover": { bgcolor: tokens.barra.itemHoverFondo },
        "&.active": {
          bgcolor: tokens.barra.itemActivoFondo,
          color: tokens.barra.itemActivoTexto,
          fontWeight: 600,
        },
      }}
    >
      <ListItemIcon>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: indent ? 13.5 : 14 }} />
      {item.badge}
    </ListItemButton>
  );
}

/**
 * El aviso de Externos: cuántos coguías externos esperan algo.
 *
 * En naranjo van los que esperan **acceso**, que es lo que se resuelve con un botón.
 * Si no queda ninguno pero sí temas sin el correo anotado, el número pasa a gris: hay
 * algo que mirar, pero no se arregla desde acá —hay que conseguir el dato—.
 */
function AvisoExternos({ pendientes, sinCorreo }: { pendientes: number; sinCorreo: number }) {
  const resaltar = pendientes > 0;
  const n = resaltar ? pendientes : sinCorreo;
  if (n === 0) return null;
  const titulo = resaltar
    ? `${pendientes} coguía(s) externo(s) esperando acceso` + (sinCorreo > 0 ? `, y ${sinCorreo} tema(s) sin correo anotado` : "")
    : `${sinCorreo} tema(s) con un coguía externo sin correo anotado`;
  return (
    <Tooltip title={titulo} placement="right">
      <Box component="span" sx={{ display: "inline-flex" }}>
        <Contador n={n} resaltar={resaltar} />
      </Box>
    </Tooltip>
  );
}

/**
 * Layout de la app autenticada (Design Kit DCC, vista B): barra lateral azul
 * con logo, navegación por permisos implícitos y datos de sesión + salir abajo.
 * Colapsa a un Drawer temporal en móvil.
 */
export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  // Pantallas de trabajo que necesitan todo el ancho: una tabla con una columna por
  // integrante de la comisión más la carga al lado no cabe en 1200 px.
  const anchoCompleto = !!useMatch("/app/comisiones/:paso/:ano/:semestre");
  const { user, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Solicitudes + revisiones que esperan al usuario. Solo docentes/coordinación:
  // el estudiante no resuelve solicitudes ni evalúa, y así no consultamos de más.
  const pendientes = usePendientesProcesos(!!user?.puede_ver_estudiantes);
  // El Examen de Título sí espera al estudiante (crear la agenda, elegir el horario) y a cada
  // integrante (responder en Miti): cuenta para todos.
  const examen = useTareasExamen().length;
  const totalPendientes = pendientes.total + examen;
  // Coguías externos esperando acceso: solo la coordinación puede dárselo.
  const { data: externos } = useExternosPendientes(!!user?.es_coordinador_global);

  const handleLogout = async () => {
    await logout();
    // Tras cerrar sesión, al home público (no al login).
    window.location.href = (import.meta as any).env?.BASE_URL || "/";
  };

  // Ítems del trabajo (arriba) y los de consulta (abajo). Estudiantes es solo del
  // coordinador (ve a todos); profesores/revisores usan Procesos, que lista sus
  // estudiantes por rol.
  const navTop: NavEntry[] = [
    {
      to: "/app",
      label: "Mis Procesos",
      icon: <AssignmentOutlined />,
      end: true,
      badge: totalPendientes > 0 && (
        <Tooltip
          title={[
            pendientes.solicitudes && `${pendientes.solicitudes} solicitud(es)`,
            pendientes.revisiones && `${pendientes.revisiones} revisión(es)`,
            examen && `${examen} pendiente(s) del Examen de Título`,
          ]
            .filter(Boolean)
            .join(" · ")}
          placement="right"
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <Contador n={totalPendientes} resaltar />
          </Box>
        </Tooltip>
      ),
    },
    ...(user?.es_coordinador_global ? [{ to: "/app/estudiantes", label: "Estudiantes", icon: <GroupsOutlined /> }] : []),
    { to: "/app/temas", label: "Temas", icon: <TopicOutlined /> },
    // Con quién trabajar: la pregunta anterior a "qué tema tomo". La ve cualquiera
    // con sesión, no solo el coordinador.
    { to: "/app/docentes", label: "Docentes", icon: <SchoolOutlined /> },
  ];
  // Subgrupo "Coordinación": herramientas del coordinador.
  const navCoordinacion: NavEntry[] = user?.es_coordinador_global
    ? [
        { to: "/app/calendario", label: "Fechas de entrega", icon: <CalendarMonthOutlined /> },
        { to: "/app/evaluadores", label: "Evaluadores", icon: <FactCheckOutlined /> },
        { to: "/app/comisiones", label: "Comisiones", icon: <GroupsOutlined /> },
        { to: "/app/pasos", label: "Pasos y evaluación", icon: <ChecklistRtlOutlined /> },
        {
          to: "/app/externos",
          label: "Externos",
          icon: <PersonSearchOutlined />,
          badge: <AvisoExternos pendientes={externos?.pendientes ?? 0} sinCorreo={externos?.sin_correo ?? 0} />,
        },
        { to: "/app/correos", label: "Correos enviados", icon: <MarkEmailReadOutlined /> },
      ]
    : [];
  // Al pie: lo que se consulta, no lo que se trabaja. El contenido del sitio
  // público (inicio y preguntas) queda inalcanzable al entrar a la app —el header
  // público desaparece—, así que se ofrece aquí, junto al perfil.
  const navPie: NavEntry[] = [
    { to: "/app/inicio", label: "Inicio", icon: <HomeOutlined /> },
    // "Preguntas" a secas: el nombre completo no cabe en una línea de la barra.
    { to: "/app/preguntas", label: "Preguntas", icon: <HelpOutlineOutlined /> },
    { to: "/app/sugerencias", label: "Sugerencias", icon: <LightbulbOutlined /> },
    { to: "/app/perfil", label: "Mi perfil", icon: <PersonOutlined /> },
  ];

  const coordActivo = navCoordinacion.some((i) => location.pathname.startsWith(i.to));
  const [coordAbierto, setCoordAbierto] = useState(true);

  const nombre = user?.first_name || user?.username || "";
  const rol = user?.es_coordinador_global
    ? "Coordinación"
    : user?.puede_ver_estudiantes
      ? "Docente"
      : "Estudiante";

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", color: tokens.barra.marca }}>
      {/* Marca */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.5, py: 2.5 }}>
        <DccLogo height={30} />
        <Typography
          sx={{
            fontFamily: tokens.fuente.titulos,
            fontWeight: 600,
            fontSize: 14,
            lineHeight: 1,
            textTransform: "uppercase",
            letterSpacing: ".08em",
          }}
        >
          Titulación
        </Typography>
      </Box>
      <Divider sx={{ borderColor: tokens.barra.divisor }} />

      {/* Navegación */}
      <List sx={{ px: 1.5, pt: 2, pb: 0, flexGrow: 1 }}>
        {navTop.map((item) => (
          <NavItem key={item.to} item={item} onNavigate={() => setMobileOpen(false)} />
        ))}

        {/* Subgrupo colapsable "Coordinación" (solo coordinador). Va tras un
            separador: son herramientas de gestión, no el trabajo de cada día. */}
        {navCoordinacion.length > 0 && (
          <>
            <Divider sx={{ borderColor: tokens.barra.divisor, my: 1 }} />
            <ListItemButton
              onClick={() => setCoordAbierto((o) => !o)}
              sx={{
                borderRadius: `${tokens.barra.radioItem}px`,
                mb: 0.5,
                color: coordActivo ? tokens.barra.itemActivoTexto : tokens.barra.itemTexto,
                "& .MuiListItemIcon-root": { color: "inherit", minWidth: 38 },
                "&:hover": { bgcolor: tokens.barra.itemHoverFondo },
              }}
            >
              <ListItemIcon>
                <TuneOutlined />
              </ListItemIcon>
              <ListItemText primary="Coordinación" primaryTypographyProps={{ fontSize: 14, fontWeight: coordActivo ? 600 : 400 }} />
              {coordAbierto ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </ListItemButton>
            <Collapse in={coordAbierto} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ mb: 0.5 }}>
                {navCoordinacion.map((item) => (
                  <NavItem key={item.to} item={item} indent onNavigate={() => setMobileOpen(false)} />
                ))}
              </List>
            </Collapse>
          </>
        )}

      </List>

      {/* Consulta (al pie, separado del trabajo del día) */}
      <Divider sx={{ borderColor: tokens.barra.divisor, mx: 1.5 }} />
      <List sx={{ px: 1.5, py: 1.5 }}>
        {navPie.map((item) => (
          <NavItem key={item.to} item={item} onNavigate={() => setMobileOpen(false)} />
        ))}
      </List>

      {/* Sesión */}
      <Divider sx={{ borderColor: tokens.barra.divisor }} />
      <Box sx={{ m: 1.5, p: 1.25, borderRadius: `${tokens.barra.radioItem}px`, bgcolor: tokens.barra.sesionFondo, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
            {nombre}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11, color: tokens.barra.sesionRol }}>
            {rol}
          </Typography>
        </Box>
        <BotonTema color={tokens.barra.itemTexto} />
        <Tooltip title="Cerrar sesión">
          <IconButton onClick={handleLogout} size="small" sx={{ color: tokens.barra.itemTexto }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Barra superior solo en móvil (abre el Drawer) */}
      <AppBar
        position="fixed"
        color="primary"
        sx={{ display: { md: "none" }, zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <DccLogo height={24} />
          <Typography sx={{ ml: 1, fontFamily: tokens.fuente.titulos, fontWeight: 700 }}>Titulación</Typography>
        </Toolbar>
      </AppBar>

      {/* Barra lateral */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box", bgcolor: tokens.barra.fondo, border: 0 },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box", bgcolor: tokens.barra.fondo, border: 0 },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Contenido */}
      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, minWidth: 0 }}>
        <Toolbar sx={{ display: { md: "none" } }} />
        {/* Contenido centrado con ancho máximo, para que no se estire en pantallas anchas. */}
        <Box sx={{ maxWidth: anchoCompleto ? "none" : 1200, mx: "auto", px: { xs: 2, sm: 4 }, py: { xs: 3, md: 5 } }}>
          <Outlet />
        </Box>
        {/* Presenta las sugerencias la primera vez; se recuerda por persona. */}
        <AvisoSugerencias />
        <SesionExpirada />
      </Box>
    </Box>
  );
}
